import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { createContext, useContext, useState } from 'react';
import { useUITheme } from '@crehana/layouts';
import { useMediaQuery, useStickyState } from '@crehana/react-hooks';
import { isProduction } from "../../constants";
import { useKnowledgeHubAvailabilityStatus } from "../../hooks/use-knowledge-hub-status";
import { useLearning, useMenuLinks, useTalentModules } from "./hooks";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
var SidebarContext = /*#__PURE__*/createContext(undefined);
export var SidebarProvider = function SidebarProvider(props) {
  var email = props.email,
    organizationSlug = props.organizationSlug,
    sidebarType = props.sidebarType,
    handleUrl = props.handleUrl,
    productPlatform = props.productPlatform,
    mobileSidebarOpen = props.mobileSidebarOpen,
    hideMobileSidebar = props.hideMobileSidebar,
    organizationId = props.organizationId,
    isGettingSidebarVersion = props.isGettingSidebarVersion;
  var _useUITheme = useUITheme(),
    isDark = _useUITheme.isDark;
  var _useStickyState = useStickyState(false, 'homeUserSidebarCollapsed'),
    _useStickyState2 = _slicedToArray(_useStickyState, 2),
    sidebarCollapsed = _useStickyState2[0],
    setSidebarCollapsed = _useStickyState2[1];
  var _useMediaQuery = useMediaQuery('sm'),
    isSm = _useMediaQuery.matches;
  var toggleSidebar = function toggleSidebar() {
    return setSidebarCollapsed(function (prev) {
      return !prev;
    });
  };

  // Current Module
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    currentModule = _useState2[0],
    setCurrentModule = _useState2[1];
  var onClickModule = function onClickModule(module) {
    setCurrentModule(module);
  };
  var onClickBackButton = function onClickBackButton() {
    setCurrentModule(null);
  };
  var _useLearning = useLearning({
      email: email
    }),
    _isLearningModuleActive = _useLearning.isLearningModuleActive,
    learningOrgSlug = _useLearning.learningOrgSlug;
  var _useKnowledgeHubAvail = useKnowledgeHubAvailabilityStatus({
      organizationId: organizationId.toString()
    }),
    hasKnowledgeHubAvailable = _useKnowledgeHubAvail.hasKnowledgeHubAvailable,
    isLoadingKnowledgeHubAvailability = _useKnowledgeHubAvail.loading;
  var _useTalentModules = useTalentModules({
      email: email,
      organizationSlug: organizationSlug,
      organizationId: organizationId,
      sidebarType: sidebarType,
      isLearningModuleActive: _isLearningModuleActive,
      isKnowledgeHubEnabled: hasKnowledgeHubAvailable
    }),
    flatModulesData = _useTalentModules.flatModulesData,
    loadingTalentModules = _useTalentModules.loading;
  var isLearningModuleActive = productPlatform === 'learning' || _isLearningModuleActive;
  var pathname = window.location.pathname;
  var organizationSlugToUse = organizationSlug || (isLearningModuleActive ? learningOrgSlug : '');
  var _useMenuLinks = useMenuLinks({
      isKnowledgeHubEnabled: hasKnowledgeHubAvailable,
      sidebarType: sidebarType,
      organizationSlug: organizationSlugToUse,
      pathname: pathname,
      userEmail: email,
      handleUrl: handleUrl,
      productPlatform: productPlatform,
      isProduction: isProduction,
      talentModules: flatModulesData,
      setCurrentModule: setCurrentModule,
      currentModule: currentModule,
      loadingTalentModules: loadingTalentModules
    }),
    menuLinks = _useMenuLinks.menuLinks,
    onClickItem = _useMenuLinks.onClickItem,
    currentSingleRoute = _useMenuLinks.currentSingleRoute,
    footerLinks = _useMenuLinks.footerLinks;
  var isLoading = isGettingSidebarVersion || loadingTalentModules || isLoadingKnowledgeHubAvailability;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(SidebarContext.Provider, {
      value: {
        email: email,
        productPlatform: productPlatform,
        menuLinks: menuLinks,
        currentSingleRoute: currentSingleRoute,
        footerLinks: footerLinks,
        isDark: isDark,
        isLoading: isLoading,
        isSm: isSm,
        mobileSidebarOpen: mobileSidebarOpen,
        hideMobileSidebar: hideMobileSidebar,
        organizationSlug: organizationSlugToUse,
        toggleSidebar: toggleSidebar,
        sidebarCollapsed: sidebarCollapsed,
        onClickItem: onClickItem,
        // Module
        currentModule: currentModule,
        onClickModule: onClickModule,
        onClickBackButton: onClickBackButton,
        // This enables the experimental sidebar animation
        isSidebarAnimationEnabled: false,
        sidebarType: sidebarType
      },
      children: props.children
    })
  });
};
export var useSidebar = function useSidebar() {
  var context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};